/**
 * The Studio welcome: the trainer's photograph, their name as large as the frame allows, the 3D
 * object in their colour, the survey tile, their message and the button that starts.
 *
 * Pure props, no I/O. The public page draws it full screen; the builder draws it in a phone or a
 * desktop frame as the live preview, and it lays out by the FRAME's width (container queries), so a
 * scaled 390px frame inside a 1440px window draws the phone composition.
 *
 * Canon: `V3-Tel-Powitanie` / `Builder-Podglad-v3` (phone) and `V3-Desk-Powitanie` (desktop).
 * The photograph's treatment is the FILE's (R2): a transparent cut-out stands on the page and the
 * name may overlap it; a photograph with its own background is dimmed and fades into the black.
 */

import { useEffect, useRef, type CSSProperties } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';

import { useScreenMotionPreference } from '~/shared/design-system/motion';

import type { PublicFormAppearance } from '../model/appearance';
import { headingScale, studioObjectSrc } from '../model/catalog';
import type { StudioImages } from '../model/images';
import {
  brandLineName,
  desktopNameSize,
  estimatedMinutes,
  nameLines,
  phoneNameSize,
  stripLines,
} from '../model/landing';
import { linksAt } from '../model/links';
import { studioMotion } from '../model/motion';
import { themeVarsFor, type AccountBrandColor } from '../model/theme';
import { StudioClockGlyph, StudioPinGlyph } from './StudioGlyphs';
import { StudioLinks } from './StudioLinks';
import {
  StudioAvatar,
  StudioBrandLine,
  StudioGrain,
  StudioKeyCap,
  StudioPill,
  StudioPoweredBy,
} from './StudioParts';
import styles from './StudioLanding.module.css';

export interface StudioLandingProps {
  readonly appearance: PublicFormAppearance;
  /** The trainer's account colour — what `color.mode === 'app'` paints. `null` reads as Flame. */
  readonly brand: AccountBrandColor | null;
  readonly images: StudioImages;
  readonly questionCount: number;
  /**
   * The start. Absent, the button is drawn but is not a control and Enter does nothing — the
   * builder's preview, which also hides the whole landing from assistive technology itself.
   */
  readonly onStart?: () => void;
}


export function StudioLanding({ appearance, brand, images, questionCount, onStart }: StudioLandingProps) {
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const rootRef = useRef<HTMLDivElement>(null);
  const { texts, strip } = appearance;
  const displayName = texts.displayName;
  const name = nameLines(displayName);
  const minutes = estimatedMinutes(questionCount);
  const slogans = strip.mode === 'slogans' ? strip.slogans : [];
  const lines = strip.mode === 'minimal' ? stripLines(strip.line) : [];
  const links = linksAt(appearance.links, 'first');
  const cta = texts.cta === '' ? t('web:publicForm.studio.ctaFallback') : texts.cta;
  const nameScale = headingScale(appearance.fonts.headingSize);

  // Enter starts, as the desktop hint under the button promises. Not while typing somewhere.
  useEffect(() => {
    if (onStart === undefined) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.defaultPrevented) return;
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('button, a, input, textarea, select')) return;
      onStart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onStart]);

  /**
   * The desktop's depth: the photograph, the name and the object drift at three rates under the
   * pointer. Written straight to the root's style — a render per pointer move would be the whole
   * landing re-rendered to change two numbers — and read only by the desktop's container query.
   */
  const drift = (x: number, y: number) => {
    const root = rootRef.current;
    if (root === null) return;
    root.style.setProperty('--studio-px', x.toFixed(3));
    root.style.setProperty('--studio-py', y.toFixed(3));
  };

  const style = {
    ...themeVarsFor(appearance, brand),
    '--studio-name-size': phoneNameSize(displayName, appearance.fonts.heading, nameScale),
    '--studio-name-size-desk': desktopNameSize(displayName, appearance.fonts.heading, nameScale),
  } as CSSProperties;

  const objectSrc =
    appearance.object.kind === 'library' && appearance.object.id !== null
      ? studioObjectSrc(appearance.object.id)
      : appearance.object.kind === 'custom'
        ? images.customObject
        : null;

  /** One line's letters, each rising a step after the one before it across both lines. */
  const letters = (word: string, offset: number) =>
    [...word.toLocaleUpperCase('pl')].map((character, index) =>
      character === ' ' ? (
        <span key={index} className={styles.space} />
      ) : (
        <span
          key={index}
          className={styles.letter}
          style={{ '--studio-letter-index': offset + index } as CSSProperties}
        >
          {character}
        </span>
      ),
    );

  return (
    <div
      ref={rootRef}
      className={styles.landing}
      style={style}
      data-studio=""
      data-studio-motion={studioMotion(appearance.motion.style, reducedMotion)}
      onPointerMove={(event) => {
        if (reducedMotion) return;
        const box = event.currentTarget.getBoundingClientRect();
        drift(
          Math.max(-1, Math.min(1, ((event.clientX - box.left) / box.width - 0.5) * 2)),
          Math.max(-1, Math.min(1, ((event.clientY - box.top) / box.height - 0.5) * 2)),
        );
      }}
      onPointerLeave={() => drift(0, 0)}
    >
      <div className={styles.stage}>
        {images.main === null ? (
          <div className={clsx(styles.disc, styles.parallaxPortrait)} aria-hidden="true" />
        ) : (
          <div
            className={clsx(
              styles.photo,
              !images.main.hasTransparency && styles.photoDimmed,
              styles.parallaxPortrait,
            )}
          >
            <img className={styles.photoImage} src={images.main.url} alt="" />
            <div className={styles.photoFade} />
          </div>
        )}

        <header className={styles.header}>
          <StudioBrandLine
            displayName={brandLineName(texts)}
            logoUrl={images.logo}
            showLogo={appearance.logo.visible}
            size="large"
            className={styles.headerBrand}
          />
          {texts.badge !== '' && <span className={styles.badge}>{texts.badge}</span>}
        </header>

        <div className={styles.objectAnchor}>
          {objectSrc !== null && (
            <div
              className={clsx(
                styles.object,
                appearance.object.kind === 'library' && styles.objectLibrary,
                styles.parallaxObject,
              )}
              aria-hidden="true"
            >
              <div className={styles.objectDrop}>
                <img className={styles.objectImage} src={objectSrc} alt="" />
              </div>
            </div>
          )}
        </div>

        <div className={clsx(styles.nameBlock, styles.parallaxName)}>
          {/* `tabIndex={-1}`: the page moves the focus to its step's heading, and a heading that
            * cannot take it leaves the focus on the body. */}
          <h1 className={styles.name} aria-label={displayName} tabIndex={-1}>
            <span className={styles.nameLine} aria-hidden="true">
              {letters(name.first, 0)}
            </span>
            {name.rest !== '' && (
              <span className={styles.nameLine} aria-hidden="true">
                {letters(name.rest, [...name.first].length)}
              </span>
            )}
          </h1>
        </div>

        {lines.length > 0 && (
          <p className={styles.stripLine}>
            <StripRows lines={lines} />
          </p>
        )}
        {texts.location !== '' && (
          <p className={styles.location}>
            <StudioPinGlyph className={styles.locationIcon} />
            <span className={styles.locationText}>{texts.location}</span>
          </p>
        )}
        {/* One list for both layouts: stacked under the location on a phone, and lifted to the
          * top left of the stage on a desktop. */}
        {slogans.length > 0 && (
          <ul className={styles.kick}>
            {slogans.map((slogan, index) => (
              <li key={index}>{slogan}.</li>
            ))}
          </ul>
        )}

        <div className={styles.column}>
          {texts.tileMessage !== '' && (
            <div className={styles.messageCard}>
              <div className={styles.cardHead}>
                <StudioAvatar displayName={displayName} url={images.profile} className={styles.cardAvatar} />
                <div className={styles.cardWho}>
                  <span className={styles.cardName}>{texts.tileSignature || displayName}</span>
                  <span className={styles.cardSub}>{t('web:publicForm.studio.messageForYou')}</span>
                </div>
              </div>
              <p className={styles.cardMessage}>{texts.tileMessage}</p>
            </div>
          )}

          <div className={styles.tiles}>
            <div className={styles.surveyTile}>
              <span className={styles.surveyLabel}>{t('web:publicForm.studio.surveyLabel')}</span>
              <span className={styles.surveyCount}>
                <span className={styles.surveyFigure}>{questionCount}</span>
                <span className={styles.surveyWord}>
                  {t('web:publicForm.studio.questionWord', { count: questionCount })}
                </span>
              </span>
              <span className={styles.surveyTime}>
                <StudioClockGlyph className={styles.surveyTimeIcon} />
                {t('web:publicForm.studio.minutes', { count: minutes })}
              </span>
            </div>

            <div className={styles.messageTile}>
              <div className={styles.signatureRow}>
                <StudioAvatar displayName={displayName} url={images.profile} className={styles.tileAvatar} />
                <span className={styles.signature}>{texts.tileSignature || name.first}</span>
              </div>
              <p className={styles.message}>{texts.tileMessage}</p>
            </div>
          </div>

          <StudioPill label={cta} onPress={onStart} className={styles.cta} />

          {onStart !== undefined && (
            <p className={styles.hint} aria-hidden="true">
              <span>{t('web:publicForm.studio.enterHint')}</span>
              <StudioKeyCap>{t('web:publicForm.studio.enterKey')}</StudioKeyCap>
            </p>
          )}
        </div>

        <div className={styles.footer}>
          <StudioLinks links={links} />
          <StudioPoweredBy label={t('web:publicForm.studio.poweredBy')} />
        </div>

        <div className={styles.bar}>
          <div className={styles.barStart}>
            {lines.length > 0 && (
              <p className={styles.barLine}>
                <StripRows lines={lines} />
              </p>
            )}
            {texts.location !== '' && (
              <p className={styles.barLocation}>
                <StudioPinGlyph className={styles.locationIcon} />
                <span className={styles.locationText}>{texts.location}</span>
              </p>
            )}
          </div>
          <StudioLinks links={links} className={styles.barLinks} />
          <StudioPoweredBy label={t('web:publicForm.studio.poweredBy')} className={styles.barMark} />
        </div>
      </div>
      <StudioGrain />
    </div>
  );
}

/**
 * The strip's lines, each its own row, as typed. A blank row the trainer left between two lines
 * keeps its height, as it did in the field they typed it in.
 */
function StripRows({ lines }: { readonly lines: readonly string[] }) {
  return lines.map((line, index) => (
    <span key={index} className={styles.stripRow}>
      {line === '' ? '\u00a0' : line}
    </span>
  ));
}
